import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { CommunityEvent, LocaleCode, LocalizedText } from "@/shared/types";

const text = (value: LocalizedText, locale: LocaleCode) =>
  value[locale] || value["en-US"];

export function EventsPage() {
  const { t, i18n } = useTranslation();
  const locale: LocaleCode = i18n.language.startsWith("es") ? "es-US" : "en-US";
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  useEffect(() => {
    void api<CommunityEvent[]>("/public/events").then(setEvents);
  }, []);
  return (
    <div className="mx-auto max-w-6xl px-5 py-16">
      <div className="max-w-2xl">
        <p className="text-sm font-bold uppercase tracking-widest text-primary">
          Hope Hangar
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
          {t("events.title")}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          {t("events.intro")}
        </p>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {events.map((event) => (
          <Card key={event.id} className="flex flex-col overflow-hidden">
            <div className="h-2 bg-secondary" />
            <CardHeader>
              <CardTitle>{text(event.title, locale)}</CardTitle>
              <p className="text-sm leading-6 text-muted-foreground">
                {text(event.description, locale)}
              </p>
            </CardHeader>
            <CardContent className="flex-1 space-y-3 text-sm">
              <p className="flex items-center gap-2">
                <CalendarDays size={17} className="text-primary" />
                {new Intl.DateTimeFormat(locale, {
                  dateStyle: "full",
                  timeStyle: "short",
                  timeZone: event.timezone,
                }).format(new Date(event.starts_at))}
              </p>
              <p className="flex items-center gap-2">
                <MapPin size={17} className="text-primary" />
                {event.venue_name}
              </p>
            </CardContent>
            <CardFooter>
              <Button asChild>
                <Link to={`/events/${event.slug}`}>{t("events.register")}</Link>
              </Button>
            </CardFooter>
          </Card>
        ))}
        {!events.length && (
          <p className="text-muted-foreground">{t("events.empty")}</p>
        )}
      </div>
    </div>
  );
}
